import { TemplateRef } from '@angular/core';
import { ObjectTools } from '../../../tools/object-tools';
import { SolBreadcrumb } from '../../../models/sol-navigation';
import { SolTab } from '../../tab/sol-tabs/sol-tabs.state';
import {
  SolMenuGroup,
  SolMenuItem,
  SolMenuPriority,
} from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { MenuTools } from '../../../tools/menu-tools';
import { SolTooltip } from '../../../models/core/sol-tooltip';
import { SolButtonType } from '../../../models/core/sol-button';

export class SolTopbarState {
  private constructor(
    public readonly loading: boolean,
    public readonly breadcrumbs: SolBreadcrumb[],
    public readonly pageNamesLoaded: boolean,
    public readonly menu: SolMenuGroup[],
    public readonly tabs: SolTab[],
    public readonly selectedTab: string,
    /** The navbar is hidden, so the topbar owns the button that slides it back in. */
    public readonly navbarHidden: boolean,
    public readonly customBreadcrumbs: boolean,
    public readonly contextActions: SolMenuGroup[],
    public readonly promotedActions: SolMenuItem[],
    public readonly breadcrumbsLoading: boolean,
    public readonly hasContextMenu: boolean,
    public readonly toolbarTemplate: TemplateRef<void> | undefined,
    public readonly breadcrumbsTemplate: TemplateRef<void> | undefined,
    public readonly breadcrumbTooltips: Record<string, SolTooltip>,
    public readonly embedded: boolean,
    public readonly visibleBreadcrumbs: SolBreadcrumb[],
    public readonly collapsed: boolean,
    public readonly collapsedOpen: boolean,
    public readonly hasTemplateLeaf: boolean,
    public readonly leadingBreadcrumbs: SolBreadcrumb[],
    public readonly leafBreadcrumb: SolBreadcrumb | undefined,
    public readonly backBreadcrumb: SolBreadcrumb | undefined,
  ) {}

  public static of(): SolTopbarState {
    return new SolTopbarState(
      true,
      [],
      false,
      [],
      [],
      '',
      false,
      false,
      [],
      [],
      true,
      false,
      undefined,
      undefined,
      {},
      false,
      [],
      false,
      false,
      false,
      [],
      undefined,
      undefined,
    );
  }

  public withCustomBreadcrumbs(customBreadcrumbs: boolean): SolTopbarState {
    if (this.customBreadcrumbs === customBreadcrumbs) {
      return this;
    }
    return this.copy({ customBreadcrumbs }).resetCollapse();
  }

  public withEmbedded(embedded: boolean): SolTopbarState {
    if (this.embedded === embedded) {
      return this;
    }
    return this.copy({ embedded })
      .recalculateVisibleBreadcrumbs()
      .recalculateBreadcrumbTooltips()
      .recalculateLeaf()
      .recalculateBack()
      .resetCollapse();
  }

  public withNavigation(
    loading: boolean,
    breadcrumbs: SolBreadcrumb[],
    pageNamesLoaded: boolean,
    tabs: SolTab[],
    selectedTab: string,
    navbarHidden: boolean,
  ): SolTopbarState {
    return this.copy({ loading, breadcrumbs, pageNamesLoaded, tabs, selectedTab, navbarHidden })
      .recalculateDerived()
      .recalculateVisibleBreadcrumbs()
      .recalculateBreadcrumbTooltips()
      .recalculateLeaf()
      .recalculateBack()
      .resetCollapse();
  }

  public withTemplates(
    toolbarTemplate: TemplateRef<void> | undefined,
    breadcrumbsTemplate: TemplateRef<void> | undefined,
  ): SolTopbarState {
    return this.copy({ toolbarTemplate, breadcrumbsTemplate }).recalculateLeaf().resetCollapse();
  }

  public withCollapsed(collapsed: boolean): SolTopbarState {
    if (this.collapsed === collapsed) {
      return this;
    }
    return this.copy({ collapsed, collapsedOpen: collapsed && this.collapsedOpen });
  }

  public withCollapsedOpen(collapsedOpen: boolean): SolTopbarState {
    return this.copy({ collapsedOpen: this.collapsed && collapsedOpen });
  }

  public withMenu(menu: SolMenuGroup[]): SolTopbarState {
    return this.copy({ menu }).recalculateMenus();
  }

  /**
   * Splits the one declared menu into the two shapes the topbar renders: the actions a page promoted
   * out of the kebab, and the kebab itself. Both halves are omitted when empty.
   */
  private recalculateMenus(): SolTopbarState {
    const items = this.menu.flatMap(group => group.items);
    const contextActions = MenuTools.filterMenuGroups(this.menu, item => !item.priority || item.priority === 'default');
    const promotedActions = [
      ...SolTopbarState.promoted(items, 'secondary', SolButtonType.secondary),
      ...SolTopbarState.promoted(items, 'primary', SolButtonType.primary),
    ];
    const hasContextMenu = contextActions.length > 0;
    return this.copy({ contextActions, promotedActions, hasContextMenu });
  }

  private static promoted(items: SolMenuItem[], priority: SolMenuPriority, type: SolButtonType): SolMenuItem[] {
    return items.filter(item => item.priority === priority).map(item => (item.type ? item : { ...item, type }));
  }

  private recalculateVisibleBreadcrumbs(): SolTopbarState {
    const firstIsTabGroup = this.breadcrumbs[0]?.embedTabGroup === true;
    const visibleBreadcrumbs = this.embedded && firstIsTabGroup ? this.breadcrumbs.slice(1) : this.breadcrumbs;
    return this.copy({ visibleBreadcrumbs });
  }

  private recalculateDerived(): SolTopbarState {
    const breadcrumbsLoading = this.loading || !this.pageNamesLoaded;
    return this.copy({ breadcrumbsLoading });
  }

  private recalculateBreadcrumbTooltips(): SolTopbarState {
    const tooltips: Record<string, SolTooltip> = {};
    for (const breadcrumb of this.visibleBreadcrumbs) {
      tooltips[breadcrumb.id] = SolTooltip.of(breadcrumb.name);
    }
    return this.copy({ breadcrumbTooltips: tooltips });
  }

  /**
   * Splits the trail into the deepest leaf (kept inline on the right) and the leading
   * segments (folded into the collapsed popover). A trailing breadcrumbs template, when
   * present, is always the deepest leaf; otherwise the last text crumb is.
   */
  private recalculateLeaf(): SolTopbarState {
    const crumbs = this.visibleBreadcrumbs;
    const hasTemplateLeaf = this.breadcrumbsTemplate !== undefined;
    const leadingBreadcrumbs = hasTemplateLeaf ? crumbs : crumbs.slice(0, -1);
    const leafBreadcrumb = hasTemplateLeaf ? undefined : crumbs[crumbs.length - 1];
    return this.copy({ hasTemplateLeaf, leadingBreadcrumbs, leafBreadcrumb });
  }

  /**
   * The back affordance goes up to the deepest ancestor in the trail: the last clickable crumb, the
   * one crumb whose destination is a real parent rather than the page the user is already on. A
   * trail without one is at its root, so no affordance is rendered.
   */
  private recalculateBack(): SolTopbarState {
    const backBreadcrumb = ObjectTools.findLast(this.visibleBreadcrumbs.filter(crumb => crumb.clickable));
    return this.copy({ backBreadcrumb });
  }

  /**
   * Resets to the expanded state so the trail re-renders fully for one measurement pass before the
   * component decides whether to collapse again. Called whenever trail content changes.
   */
  private resetCollapse(): SolTopbarState {
    if (!this.collapsed && !this.collapsedOpen) {
      return this;
    }
    return this.copy({ collapsed: false, collapsedOpen: false });
  }

  private copy(props: Partial<SolTopbarState>): SolTopbarState {
    return ObjectTools.copy<SolTopbarState>(this, props);
  }
}
